import { Household } from '../../api/householdsApi';
import { Listener } from '../../api/listenersApi';
import { Voice } from '../../context/AppContext';

export type BedtimeContextStatus =
  | 'loading'
  | 'needs_selection'
  | 'ready'
  | 'missing_bookends'
  | 'no_listener'
  | 'no_ready_voice'
  | 'error';

export interface BedtimePair {
  householdId: string;
  listenerId: string;
  voiceId: string;
}

export interface PairBookendState {
  intro: boolean;
  goodnight: boolean;
}

export type PairBookends = Record<string, PairBookendState>;

export interface StoredBedtimeContext {
  version: 1;
  householdId: string;
  listenerId: string;
  voiceId: string;
  updatedAt: string;
}

export interface BedtimeContextModel {
  offline?: boolean;
  status: BedtimeContextStatus;
  pair: BedtimePair | null;
  households: Household[];
  listeners: Listener[];
  voices: Voice[];
  missingBookends: Array<'intro' | 'goodnight'>;
  notice: string | null;
  error: unknown | null;
}

export interface DeriveInput {
  households: Household[];
  voices: Voice[];
  listeners: Listener[];
  pairBookends?: PairBookends;
  stored?: StoredBedtimeContext | null;
}

const activeHouseholds = (households: Household[]) => households.filter((household) => household.state === 'active');
const activeListeners = (listeners: Listener[]) => listeners.filter((listener) => listener.state === 'active');

function matchingVoiceHousehold(voice: Voice, householdId: string): boolean {
  // Household identity is an authorization-scoped stable ID. A null legacy
  // value is a readiness defect and must not be inferred from household count.
  return voice.household_id === householdId;
}

function pairKey(voiceId: string, listenerId: string): string {
  return `${voiceId}:${listenerId}`;
}

export function deriveBedtimeContext(input: DeriveInput): BedtimeContextModel {
  const households = activeHouseholds(input.households);
  const allListeners = activeListeners(input.listeners);
  const accessibleVoices = input.voices;
  const readyVoices = accessibleVoices.filter((voice) => voice.status === 'ready' && voice.consent_active === true);

  if (households.length === 0 || allListeners.length === 0) {
    return { status: 'no_listener', pair: null, households, listeners: allListeners, voices: accessibleVoices, missingBookends: [], notice: null, error: null };
  }
  if (readyVoices.length === 0) {
    return { status: 'no_ready_voice', pair: null, households, listeners: allListeners, voices: accessibleVoices, missingBookends: [], notice: null, error: null };
  }

  const stored = input.stored ?? null;
  const storedHousehold = stored && households.find((household) => household.household_id === stored.householdId);
  let household = storedHousehold;
  let notice: string | null = null;
  if (!household) {
    if (households.length !== 1) {
      return { status: 'needs_selection', pair: null, households, listeners: allListeners, voices: accessibleVoices, missingBookends: [], notice: null, error: null };
    }
    household = households[0];
    if (stored?.householdId) notice = 'Your family changed. Confirm the listener and voice.';
  }

  const houseListeners = allListeners.filter((listener) => listener.household_id === household!.household_id);
  const houseVoices = accessibleVoices.filter((voice) => matchingVoiceHousehold(voice, household!.household_id));
  const houseReadyVoices = houseVoices.filter((voice) => voice.status === 'ready' && voice.consent_active === true);
  if (houseListeners.length === 0) {
    return { status: 'no_listener', pair: null, households, listeners: allListeners, voices: accessibleVoices, missingBookends: [], notice, error: null };
  }
  if (houseReadyVoices.length === 0) {
    return { status: 'no_ready_voice', pair: null, households, listeners: allListeners, voices: accessibleVoices, missingBookends: [], notice, error: null };
  }

  let voice = houseReadyVoices.find((candidate) => candidate.voice_id === stored?.voiceId);
  if (!voice) {
    if (houseReadyVoices.length !== 1) {
      return { status: 'needs_selection', pair: null, households, listeners: allListeners, voices: accessibleVoices, missingBookends: [], notice, error: null };
    }
    voice = houseReadyVoices[0];
    if (stored?.voiceId) notice = notice || 'Your family voice changed. Confirm your bedtime choices.';
  }

  let listener = houseListeners.find((candidate) => candidate.listener_id === stored?.listenerId);
  if (!listener) {
    if (houseListeners.length !== 1) {
      return { status: 'needs_selection', pair: null, households, listeners: allListeners, voices: accessibleVoices, missingBookends: [], notice, error: null };
    }
    listener = houseListeners[0];
    if (stored?.listenerId) notice = notice || 'Your listener changed. Confirm your bedtime choices.';
  }

  const pair: BedtimePair = {
    householdId: household.household_id,
    listenerId: listener.listener_id,
    voiceId: voice.voice_id,
  };
  const bookends = input.pairBookends?.[pairKey(pair.voiceId, pair.listenerId)];
  const missingBookends: Array<'intro' | 'goodnight'> = [];
  if (!bookends?.intro) missingBookends.push('intro');
  if (!bookends?.goodnight) missingBookends.push('goodnight');

  return {
    status: missingBookends.length ? 'missing_bookends' : 'ready',
    pair,
    households,
    // Keep the complete authorized option set available to the atomic pair
    // dialog. Route content still reads only the committed pair IDs.
    listeners: allListeners,
    voices: accessibleVoices,
    missingBookends,
    notice,
    error: null,
  };
}

export function replacePair(model: BedtimeContextModel, pair: BedtimePair): BedtimeContextModel {
  const household = model.households.find((item) => item.household_id === pair.householdId);
  const listener = model.listeners.find((item) => item.listener_id === pair.listenerId);
  const voice = model.voices.find((item) => item.voice_id === pair.voiceId);
  if (!household || !listener || listener.household_id !== household.household_id || !voice) return model;
  if (voice.status !== 'ready' || voice.consent_active !== true) return model;
  if (voice.household_id !== household.household_id) return model;
  // The derived model already carries the correct missing-bookend state. Do
  // not turn a valid-but-incomplete pair into an apparently ready one.
  return { ...model, pair, notice: null, error: null };
}

export { pairKey };
